import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Button,
  Form,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faEye } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../common/Pagination';
import {
  fetchDeviceActivitySummary,
  fetchDeviceActivityTimeline,
} from '../../services/mobileActivityLogApi';
import { formatDateTime, methodVariant, statusVariant } from './activityLogUtils';

const TIMELINE_PER_PAGE = 50;

const StatTile = ({ label, value, hint }) => (
  <Col xs={6} md={3} className="mb-3">
    <Card className="h-100">
      <Card.Body className="py-2 px-3">
        <div className="text-muted small">{label}</div>
        <div className="h4 mb-0">{value}</div>
        {hint && <div className="text-muted small">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

/**
 * Full behaviour trace for a single device: aggregate stats plus the
 * chronological list of API calls it made.
 */
const DeviceTraceModal = ({ show, deviceId, filters = {}, onClose, onViewLog }) => {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [timeline, setTimeline] = useState([]);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [page, setPage] = useState(1);

  // When on, the trace ignores the filters active on the list page and shows
  // everything the device did within the retention window.
  const [ignoreFilters, setIgnoreFilters] = useState(true);

  const effectiveFilters = ignoreFilters ? {} : filters;

  const loadSummary = useCallback(async () => {
    if (!deviceId) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await fetchDeviceActivitySummary({ ...effectiveFilters, device_id: deviceId });
      if (res.data?.success) {
        setSummary(res.data.data);
      } else {
        setSummaryError(res.data?.message || 'Failed to load device summary');
      }
    } catch (err) {
      setSummaryError(err.response?.data?.message || err.message || 'Failed to load device summary');
    } finally {
      setSummaryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, JSON.stringify(effectiveFilters)]);

  const loadTimeline = useCallback(async () => {
    if (!deviceId) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const res = await fetchDeviceActivityTimeline({
        ...effectiveFilters,
        device_id: deviceId,
        page,
        items_per_page: TIMELINE_PER_PAGE,
      });
      if (res.data?.success && res.data?.data) {
        setTimeline(Array.isArray(res.data.data.items) ? res.data.data.items : []);
        setTimelineTotal(res.data.data.objects?.total ?? 0);
      } else {
        setTimeline([]);
        setTimelineTotal(0);
      }
    } catch (err) {
      setTimelineError(err.response?.data?.message || err.message || 'Failed to load timeline');
      setTimeline([]);
      setTimelineTotal(0);
    } finally {
      setTimelineLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, page, JSON.stringify(effectiveFilters)]);

  useEffect(() => {
    if (!show) return;
    setPage(1);
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId, ignoreFilters]);

  useEffect(() => {
    if (!show) return;
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId, page, ignoreFilters]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
          Device trace
          {deviceId ? (
            <code className="ms-2 small">{deviceId}</code>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          type="switch"
          id="device-trace-ignore-filters"
          className="mb-3"
          label="Show all activity for this device (ignore the filters on the list page)"
          checked={ignoreFilters}
          onChange={(e) => setIgnoreFilters(e.target.checked)}
        />

        {summaryError && (
          <Alert variant="danger" className="mb-3">
            {summaryError}
          </Alert>
        )}

        {summaryLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : summary ? (
          <>
            <Row>
              <StatTile label="Requests" value={summary.total_requests ?? 0} />
              <StatTile
                label="Success rate"
                value={summary.success_rate != null ? `${summary.success_rate}%` : '—'}
                hint={`${summary.failed_requests ?? 0} failed`}
              />
              <StatTile
                label="Active days"
                value={summary.active_days ?? 0}
                hint={`${summary.distinct_endpoints ?? 0} endpoints`}
              />
              <StatTile
                label="Avg duration"
                value={summary.avg_duration_ms != null ? `${summary.avg_duration_ms} ms` : '—'}
              />
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="text-muted small">First seen</div>
                <div>{formatDateTime(summary.first_seen)}</div>
              </Col>
              <Col md={6}>
                <div className="text-muted small">Last seen</div>
                <div>{formatDateTime(summary.last_seen)}</div>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header className="py-2">Top endpoints</Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: '260px' }}>
                      <Table size="sm" hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Endpoint</th>
                            <th className="text-end">Hits</th>
                            <th className="text-end">Failed</th>
                            <th className="text-end">Avg ms</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(summary.top_endpoints || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-3">
                                No data
                              </td>
                            </tr>
                          ) : (
                            summary.top_endpoints.map((row, idx) => (
                              <tr key={`${row.method}-${row.route_uri}-${idx}`}>
                                <td>
                                  <Badge bg={methodVariant(row.method)} className="text-white me-1">
                                    {row.method}
                                  </Badge>
                                  <code className="small">{row.route_uri || '—'}</code>
                                </td>
                                <td className="text-end">{row.hits}</td>
                                <td className="text-end">{row.failures}</td>
                                <td className="text-end">{row.avg_duration_ms ?? '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="mb-3">
                  <Card.Header className="py-2">Accounts seen on this device</Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: '120px' }}>
                      <Table size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th className="text-end">Hits</th>
                            <th>Last seen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(summary.identities || []).length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center text-muted py-3">
                                Anonymous only
                              </td>
                            </tr>
                          ) : (
                            summary.identities.map((row, idx) => (
                              <tr key={`${row.user_id}-${idx}`}>
                                <td>
                                  <div>{row.email || '—'}</div>
                                  <div className="text-muted small">ID: {row.user_id}</div>
                                </td>
                                <td className="text-end">{row.hits}</td>
                                <td>{formatDateTime(row.last_seen)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header className="py-2">App builds</Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: '120px' }}>
                      <Table size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Platform</th>
                            <th>App</th>
                            <th>OS</th>
                            <th>Model</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(summary.clients || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-3">
                                No data
                              </td>
                            </tr>
                          ) : (
                            summary.clients.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.platform || '—'}</td>
                                <td>{row.app_version || '—'}</td>
                                <td>{row.os_version || '—'}</td>
                                <td>{row.device_model || '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}

        <Card>
          <Card.Header className="py-2">Timeline</Card.Header>
          <Card.Body>
            {timelineError && (
              <Alert variant="danger" className="mb-3">
                {timelineError}
              </Alert>
            )}

            <Pagination
              currentPage={page}
              totalItems={timelineTotal}
              itemsPerPage={TIMELINE_PER_PAGE}
              onPageChange={setPage}
              position="top"
            />

            {timelineLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="table-vcenter mb-0">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Endpoint</th>
                      <th>Status</th>
                      <th className="text-end">ms</th>
                      <th>User</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No activity recorded for this device
                        </td>
                      </tr>
                    ) : (
                      timeline.map((row) => (
                        <tr key={row.id}>
                          <td className="text-nowrap">{formatDateTime(row.created_at)}</td>
                          <td>
                            <Badge bg={methodVariant(row.method)} className="text-white me-1">
                              {row.method}
                            </Badge>
                            <code className="small">{row.path}</code>
                            {row.error_message && (
                              <div className="text-danger small text-truncate" style={{ maxWidth: '320px' }}>
                                {row.error_message}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg={statusVariant(row.status_code)} className="text-white">
                              {row.status_code}
                            </Badge>
                          </td>
                          <td className="text-end">{row.duration_ms}</td>
                          <td className="small">{row.email || (row.user_id ? `#${row.user_id}` : '—')}</td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => onViewLog && onViewLog(row.id)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            <Pagination
              currentPage={page}
              totalItems={timelineTotal}
              itemsPerPage={TIMELINE_PER_PAGE}
              onPageChange={setPage}
              position="bottom"
            />
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeviceTraceModal;
